import { Box, Typography } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"

import type { SourceChangeApprovalRow } from "../types"

import { Button } from "@/components/common/Button"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { ComputedProductStatus } from "@/graphql/codegen/graphql"
import { SourceChangeStatusChip } from "@/screens/Products/components/SourceChanges/components/SourceChangeStatusChip"
import { AddressFormat } from "@/types/common"
import { formatUserName } from "@/utils/user"
import { formatLocationAddress } from "@/utils/utils"

interface UseSourceChangeColumnsProps {
  onApprove: (sourceChange: SourceChangeApprovalRow) => void
  onReject: (sourceChange: SourceChangeApprovalRow) => void
}

export const useSourceChangeColumns = ({
  onApprove,
  onReject,
}: UseSourceChangeColumnsProps): ColumnDef<SourceChangeApprovalRow>[] => [
  {
    accessorKey: "productId",
    header: "Product ID",
    cell: ({ row }) => {
      const { product } = row.original
      const isActive = product?.computedProductStatus === ComputedProductStatus.Active

      return (
        <Box className='flex flex-col items-start gap-y-1'>
          <Box className='flex items-center gap-x-2'>
            {isActive && <Box className='size-2 rounded-full bg-success-500' />}
            <Typography variant='body2'>{product?.publicId}</Typography>
          </Box>
          <DisplayAS400Id id={product?.externalSystemId} />
        </Box>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => (
      <Box className='flex flex-col'>
        <Typography variant='body2' className='font-medium'>
          {row.original.product?.companyProductDescription}
        </Typography>
        <Typography variant='body2' className='text-gray-700'>
          {row.original.product?.vendorDescription}
        </Typography>
      </Box>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "companyName",
    header: "Company",
    cell: ({ row }) => row.original.record.company?.name ?? null,
    enableSorting: false,
  },
  {
    accessorKey: "location",
    header: "Location",
    cell: ({ row }) => formatLocationAddress(row.original.record.shipToLocation, AddressFormat.Medium) || null,
    enableSorting: false,
  },
  {
    accessorKey: "productManager",
    header: "Product Manager",
    cell: ({ row }) => formatUserName(row.original.product?.classification?.productManager) || null,
    enableSorting: false,
  },
  {
    accessorKey: "approvalStatus",
    header: "Approval Status",
    cell: ({ row }) => (
      <SourceChangeStatusChip
        status={row.original.record.approvalStatus}
        data-testid={`dashboard.source-change-table.status-chip-${row.original.record.id}`}
      />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "actions",
    header: "",
    cell: ({ row }) => (
      <Box className='flex items-center justify-end gap-x-2'>
        <Button
          size='small'
          appearance='outlined'
          onClick={() => onApprove(row.original)}
          data-testid={`dashboard.source-change-table.approve-button-${row.original.record.id}`}
        >
          Approve
        </Button>
        <Button
          size='small'
          appearance='text'
          onClick={() => onReject(row.original)}
          data-testid={`dashboard.source-change-table.reject-button-${row.original.record.id}`}
        >
          Reject
        </Button>
      </Box>
    ),
    size: 50,
    enableSorting: false,
  },
]
